/**
 * Formats a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'MXN')
 * @param locale - Locale for formatting (default: 'es-MX')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency = 'MXN',
  locale = 'es-MX',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Formats cents as currency (divides by 100 first)
 * @param cents - The amount in cents
 * @param currency - Currency code (default: 'MXN')
 * @param locale - Locale for formatting (default: 'es-MX')
 * @returns Formatted currency string
 */
export function formatCurrencyFromCents(
  cents: number,
  currency = 'MXN',
  locale = 'es-MX',
): string {
  return formatCurrency(cents / 100, currency, locale);
}

/**
 * Converts a pesos amount to centavos (multiplies by 100, rounds to avoid floating-point errors)
 * @param pesos - The amount in pesos
 * @returns Amount in centavos as an integer
 */
export function toCents(pesos: number): number {
  return Math.round(pesos * 100);
}

/**
 * Reads an optional cents amount, coalescing a missing one to 0.
 *
 * Money moves in integer cents and only display divides by 100. Pricing fields
 * are optional on several domain types, so an absent one must not propagate NaN
 * through a whole total. The warning surfaces the upstream payload gap that a
 * silent 0 would otherwise hide.
 *
 * @param value - The amount in cents, possibly absent
 * @param context - Dotted path of the field being read, for the warning
 * @returns The amount in cents, or 0 when absent
 */
export function centsOf(value: number | undefined, context: string): number {
  if (value === undefined || Number.isNaN(value)) {
    console.warn(`[money] missing ${context}; treating as 0`);
    return 0;
  }
  return value;
}

/**
 * Formats a number with locale-specific formatting
 * @param value - The number to format
 * @param locale - Locale for formatting (default: 'es-MX')
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted number string
 */
export function formatNumber(
  value: number,
  locale = 'es-MX',
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}
